const {
  REACT_APP_BASE_URL = 'https://quickcam.app',
  REACT_APP_QUICKIE_BASE_URL = 'https://quickiecoin.meme',
  REACT_APP_CRYPTOBULLS_BASE_URL = 'http://cryptobulls.vip',
  REACT_APP_WEBSOCKET_URL = '',
  REACT_APP_KEYCLOAK_ACCOUNT = 'https://account.promanager.vip/realms/main',
  REACT_APP_KEYCLOAK_CLIENT_ID = 'quickcam',
  REACT_APP_KEYCLOAK_REDIRECT_URI = 'https://quickcam.app',
  REACT_APP_APP_REF_URL = '',
  REACT_APP_API_BASE_URL = 'https://api.promanager.vip',
  REACT_APP_APPLICATION_DOMAIN = '',
  REACT_APP_ONBOARDING_API_BASE_URL = '',
  REACT_APP_OPENREPLAY_PROJECT_KEY = '',
  REACT_APP_OPENREPLAY_INGEST_POINT = '',
  REACT_APP_FACEBOOK_APP_ID = '223997756367248',
  REACT_APP_GOOGLE_CLIENT_ID = '867114240537-9h5moptgthqk158goqso1ufhi2noq0rl.apps.googleusercontent.com',
  REACT_APP_OPENCAGE_DATA_MAP_KEY = '',
  REACT_APP_HELPDESK_API_URL = 'https://api-alpha-admin-panel.promanager.online',
  REACT_APP_QUICKCAM_COIN_URL = ''
} = process?.env;

export function isConfiguredHttpUrl(url?: string): boolean {
  return /^https?:\/\//i.test(String(url || '').trim());
}

export function isConfiguredKeycloakUrl(url?: string): boolean {
  return isConfiguredHttpUrl(url);
}

export type QuickcamCoinStage = 'local' | 'test' | 'dev' | 'prod';

export const QUICKCAM_COIN_URLS: Record<QuickcamCoinStage, string> = {
  local: 'http://localhost:3006',
  test: 'https://test.quickcamcoin.com',
  dev: 'https://dev.quickcamcoin.com',
  prod: 'https://quickcamcoin.com',
};

export const DEFAULT_QUICKCAM_COIN_URL = QUICKCAM_COIN_URLS.prod;

const QC_STAGES: QuickcamCoinStage[] = ['local', 'test', 'dev', 'prod'];

function hostnameFrom(href: string): string {
  try {
    return new URL(href).hostname.toLowerCase();
  } catch {
    return '';
  }
}

function isLocalHost(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '::1' ||
    hostname.endsWith('.localhost')
  );
}

function isQuickcamCoinStage(value: string): value is QuickcamCoinStage {
  return QC_STAGES.includes(value as QuickcamCoinStage);
}

/** Localhost-only simulator: ?qcStage=local|test|dev|prod */
export function readLocalQcStageOverride(
  href = typeof window !== 'undefined' ? window.location.href : '',
): QuickcamCoinStage | null {
  let parsed: URL;
  try {
    parsed = new URL(href);
  } catch {
    return null;
  }
  if (!isLocalHost(parsed.hostname.toLowerCase())) {
    return null;
  }
  const value = parsed.searchParams.get('qcStage')?.trim().toLowerCase() || '';
  return isQuickcamCoinStage(value) ? value : null;
}

export type LocalQcRegion = 'allowed' | 'restricted';

export interface LocalQcLocationSim {
  region?: LocalQcRegion;
  countryCode?: string;
  stateName?: string;
  cityName?: string;
}

function parseLocalHref(href: string): URL | null {
  let parsed: URL;
  try {
    parsed = new URL(href);
  } catch {
    return null;
  }
  if (!isLocalHost(parsed.hostname.toLowerCase())) {
    return null;
  }
  return parsed;
}

/** Localhost-only: ?qcRegion=allowed|restricted and optional qcCountry/qcState/qcCity */
export function readLocalQcLocationSim(
  href = typeof window !== 'undefined' ? window.location.href : '',
): LocalQcLocationSim | null {
  const parsed = parseLocalHref(href);
  if (!parsed) {
    return null;
  }
  const regionRaw = parsed.searchParams.get('qcRegion')?.trim().toLowerCase() || '';
  const region: LocalQcRegion | undefined =
    regionRaw === 'allowed' || regionRaw === 'restricted' ? regionRaw : undefined;
  const countryCode = parsed.searchParams.get('qcCountry')?.trim() || undefined;
  const stateName = parsed.searchParams.get('qcState')?.trim() || undefined;
  const cityName = parsed.searchParams.get('qcCity')?.trim() || undefined;
  if (!region && !countryCode && !stateName && !cityName) {
    return null;
  }
  return { region, countryCode, stateName, cityName };
}

export function getQuickcamCoinStage(
  href = typeof window !== 'undefined' ? window.location.href : '',
): QuickcamCoinStage {
  const override = readLocalQcStageOverride(href);
  if (override) {
    return override;
  }
  const hostname = hostnameFrom(href);
  if (isLocalHost(hostname)) {
    return 'local';
  }
  if (hostname.startsWith('test.') || hostname.includes('.test.')) {
    return 'test';
  }
  if (hostname.startsWith('dev.') || hostname.includes('.dev.')) {
    return 'dev';
  }
  return 'prod';
}

function isProductionCoinUrl(url: string): boolean {
  const hostname = hostnameFrom(url);
  return hostname === 'quickcamcoin.com' || hostname === 'www.quickcamcoin.com';
}

/** Real QuickCam Coin URL for the current app stage. */
export function getQuickcamCoinUrl(
  rawUrl = REACT_APP_QUICKCAM_COIN_URL,
  href = typeof window !== 'undefined' ? window.location.href : '',
): string {
  const override = readLocalQcStageOverride(href);
  if (override) {
    return QUICKCAM_COIN_URLS[override];
  }
  const stage = getQuickcamCoinStage(href);
  const trimmed = String(rawUrl || '').trim();
  if (isConfiguredHttpUrl(trimmed)) {
    if (isProductionCoinUrl(trimmed) && stage !== 'prod') {
      return QUICKCAM_COIN_URLS[stage];
    }
    if (isLocalHost(hostnameFrom(trimmed)) && stage === 'local') {
      return QUICKCAM_COIN_URLS.local;
    }
    return trimmed.replace(/\/$/, '');
  }
  return QUICKCAM_COIN_URLS[stage];
}

const ENVIRONMENTS = {
  BASE_URL: REACT_APP_BASE_URL,
  WEBSOCKET_URL: REACT_APP_WEBSOCKET_URL,
  KEYCLOAK_ACCOUNT: REACT_APP_KEYCLOAK_ACCOUNT,
  KEYCLOAK_CLIENT_ID: REACT_APP_KEYCLOAK_CLIENT_ID,
  KEYCLOAK_REDIRECT_URI: REACT_APP_KEYCLOAK_REDIRECT_URI,
  APPLICATION_DOMAIN: REACT_APP_APPLICATION_DOMAIN,
  ONBOARDING_API_BASE_URL: REACT_APP_ONBOARDING_API_BASE_URL,
  OPENREPLAY_INGEST_POINT: REACT_APP_OPENREPLAY_INGEST_POINT,
  OPENREPLAY_PROJECT_KEY: REACT_APP_OPENREPLAY_PROJECT_KEY,
  API_BASE_URL_V2: REACT_APP_API_BASE_URL,
  OPENCAGE_DATA_MAP_KEY: 'b351f02913f34f95bf8b86d091ff586a',
  FACEBOOK_APP_ID: REACT_APP_FACEBOOK_APP_ID,
  GOOGLE_CLIENT_ID: REACT_APP_GOOGLE_CLIENT_ID,
  HELPDESK_API_URL: 'https://api-alpha-admin-panel.promanager.online',
  APP_REF_URL: 'http://localhost:4000',
  QUICKIE_BASE_URL:REACT_APP_QUICKIE_BASE_URL,
  CRYPTOBULLS_BASE_URL:REACT_APP_CRYPTOBULLS_BASE_URL,
  QUICKCAM_COIN_URL: getQuickcamCoinUrl(REACT_APP_QUICKCAM_COIN_URL)
};

/** OAuth callback URI. Must match the Keycloak client and the token exchange. */
export function getKeycloakRedirectUri(
  origin = typeof window !== 'undefined' ? window.location.origin : '',
): string {
  // Loopback tabs must keep host+port (127.0.0.1 vs localhost). A baked
  // localhost redirect_uri from a 127.0.0.1 page sends Keycloak to the
  // other host; the API then exchanges against localhost:8080 which hangs
  // on IPv6 while 127.0.0.1:8080 answers immediately.
  if (origin) {
    try {
      const page = new URL(origin);
      const host = page.hostname.toLowerCase();
      if (host === 'localhost' || host === '127.0.0.1') {
        return `${page.origin.replace(/\/$/, '')}/redirect`;
      }
    } catch {
      /* fall through to configured URI */
    }
  }
  if (isConfiguredKeycloakUrl(ENVIRONMENTS.KEYCLOAK_REDIRECT_URI)) {
    return ENVIRONMENTS.KEYCLOAK_REDIRECT_URI.replace(/\/$/, '');
  }
  return origin ? `${origin.replace(/\/$/, '')}/redirect` : '';
}

/** Same Keycloak auth URL the landing Login button uses (SSO if the session is live). */
export function getKeycloakLoginUrl(from = 'Login'): string {
  const redirectUri = getKeycloakRedirectUri();
  return `${ENVIRONMENTS.KEYCLOAK_ACCOUNT}/protocol/openid-connect/auth?client_id=${ENVIRONMENTS.KEYCLOAK_CLIENT_ID}&response_mode=fragment&response_type=code&scope=${encodeURIComponent('openid email profile')}&login=true&redirect_uri=${encodeURIComponent(
    redirectUri,
  )}&from=${encodeURIComponent(from)}`;
}

export function getKeycloakSilentAuthUrl(state: string): string {
  return `${ENVIRONMENTS.KEYCLOAK_ACCOUNT}/protocol/openid-connect/auth?client_id=${ENVIRONMENTS.KEYCLOAK_CLIENT_ID}&response_mode=fragment&response_type=code&scope=${encodeURIComponent('openid email profile')}&prompt=none&state=${encodeURIComponent(
    state,
  )}&redirect_uri=${encodeURIComponent(getKeycloakRedirectUri())}`;
}

export const QC_AFTER_SSO_LOGOUT_KEY = 'qc_after_sso_logout';
export const QC_SSO_CLEARED_KEY = 'qc_sso_cleared';

export function getKeycloakRegistrationUrl(
  redirectUri = getKeycloakRedirectUri(),
): string {
  return `${ENVIRONMENTS.KEYCLOAK_ACCOUNT}/protocol/openid-connect/registrations?client_id=${ENVIRONMENTS.KEYCLOAK_CLIENT_ID}&response_mode=fragment&response_type=code&scope=${encodeURIComponent('openid email profile')}&login=true&redirect_uri=${encodeURIComponent(
    redirectUri,
  )}&from=Signup`;
}

/** End the browser Keycloak session, then return to the app. */
export function getKeycloakLogoutUrl(
  postLogoutRedirectUri: string = typeof window !== 'undefined'
    ? `${window.location.origin}/`
    : '',
  idTokenHint?: string,
): string {
  const afterLogout = postLogoutRedirectUri || '/';
  let url = `${ENVIRONMENTS.KEYCLOAK_ACCOUNT}/protocol/openid-connect/logout?client_id=${encodeURIComponent(
    String(ENVIRONMENTS.KEYCLOAK_CLIENT_ID || ''),
  )}&post_logout_redirect_uri=${encodeURIComponent(afterLogout)}`;
  if (idTokenHint) {
    url += `&id_token_hint=${encodeURIComponent(idTokenHint)}`;
  }
  return url;
}

export default ENVIRONMENTS;
